import React, { useState } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { COMPONENT_REGISTRY } from '../registry/componentRegistry';
import { AFTER_PREFIX, CONTAINER_PREFIX } from '../dragEndLogic';

/**
 * Tree view of the node hierarchy — click to select, drag to reparent.
 * Reuses the EXACT same drag/drop id scheme as LandingBuilder.jsx's
 * CanvasNode ('after:<id>' / 'container:<id>' droppables, raw node id as
 * the draggable id) so dragging a row here is handled by the same single
 * top-level DndContext/handleDragEnd in LandingBuilder.jsx — no separate
 * drag logic needed, and dragging between the Navigator and the canvas
 * works interchangeably since both register into the same context.
 */
export default function NavigatorPanel({ nodes, selectedId, onSelect }) {
    return (
        <div className="lb-navigator">
            <h4>Layers</h4>
            {nodes.length === 0 ? (
                <p className="lb-inspector-empty">No components yet.</p>
            ) : (
                <div className="lb-navigator-tree">
                    {nodes.map((node) => (
                        <NavigatorRow key={node.id} node={node} depth={0} selectedId={selectedId} onSelect={onSelect} />
                    ))}
                </div>
            )}
        </div>
    );
}

function NavigatorRow({ node, depth, selectedId, onSelect }) {
    const [expanded, setExpanded] = useState(true);
    const def = COMPONENT_REGISTRY[node.type];
    const hasChildren = def?.isContainer && (node.children || []).length > 0;

    const drag = useDraggable({ id: node.id });
    const dropAfter = useDroppable({ id: AFTER_PREFIX + node.id });
    const dropInto = useDroppable({ id: CONTAINER_PREFIX + node.id });

    return (
        <div ref={dropAfter.setNodeRef} className={dropAfter.isOver ? 'lb-drop-over' : ''}>
            <div
                ref={drag.setNodeRef}
                className={`lb-nav-row ${selectedId === node.id ? 'selected' : ''} ${drag.isDragging ? 'dragging' : ''}`}
                style={{ paddingLeft: `${depth * 16 + 8}px` }}
                onClick={() => onSelect(node.id)}
            >
                {def?.isContainer ? (
                    <button
                        type="button"
                        className="lb-nav-toggle"
                        onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
                    >
                        <i className={`fas fa-chevron-${expanded ? 'down' : 'right'}`}></i>
                    </button>
                ) : (
                    <span className="lb-nav-toggle-spacer" />
                )}
                <span className="lb-nav-handle" {...drag.listeners} {...drag.attributes}>
                    <i className="fas fa-grip-vertical"></i>
                </span>
                <i className={`fas ${def?.icon || 'fa-cube'}`}></i>
                <span className="lb-nav-label">{def?.label || node.type}</span>
            </div>
            {def?.isContainer && (
                <div ref={dropInto.setNodeRef} className={dropInto.isOver ? 'lb-drop-over' : ''}>
                    {expanded && hasChildren && node.children.map((child) => (
                        <NavigatorRow key={child.id} node={child} depth={depth + 1} selectedId={selectedId} onSelect={onSelect} />
                    ))}
                </div>
            )}
        </div>
    );
}
